import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Release } from '../lib/api';
import { Download, FileSignature, MapPin, Monitor, CalendarClock, Mail, User } from 'lucide-react';

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-stone-100 last:border-0">
      <div className="w-8 h-8 rounded-md grid place-items-center bg-stone-100 text-stone-600 shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10.5px] uppercase tracking-wider text-stone-500 font-semibold mb-0.5">{label}</div>
        <div className="text-[13px] text-stone-900 break-words">{children}</div>
      </div>
    </div>
  );
}

export default function ReleaseSubmissionDialog({ submission, open, onOpenChange }) {
  if (!submission) return null;

  const fmt = (v) => {
    if (!v) return '—';
    try { return new Date(v).toLocaleString(); } catch { return v; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-stone-200 bg-stone-50">
          <DialogHeader>
            <DialogTitle className="text-[16px] font-serif flex items-center gap-2">
              <FileSignature className="w-4 h-4 text-emerald-600" /> Signed Release
            </DialogTitle>
          </DialogHeader>
          <div className="text-[12px] text-stone-500 mt-0.5">{submission.showName}{submission.episodeTitle ? ` — ${submission.episodeTitle}` : ''}</div>
        </div>

        <div className="px-5 py-2 max-h-[60vh] overflow-y-auto">
          <Row icon={User} label="Full legal name">{submission.fullName}</Row>
          <Row icon={Mail} label="Email">{submission.email}</Row>
          {submission.company && <Row icon={User} label="Company">{submission.company}</Row>}
          {submission.phone && <Row icon={User} label="Phone">{submission.phone}</Row>}
          {(submission.instagram || submission.twitter || submission.linkedin || submission.facebook) && (
            <Row icon={User} label="Social handles">
              <div className="space-y-0.5 text-[12.5px]">
                {submission.instagram && <div><span className="text-stone-500">Instagram:</span> {submission.instagram}</div>}
                {submission.twitter && <div><span className="text-stone-500">Twitter / X:</span> {submission.twitter}</div>}
                {submission.linkedin && <div><span className="text-stone-500">LinkedIn:</span> {submission.linkedin}</div>}
                {submission.facebook && <div><span className="text-stone-500">Facebook:</span> {submission.facebook}</div>}
              </div>
            </Row>
          )}
          <Row icon={FileSignature} label="Digital signature">
            <span className="font-serif italic text-[18px] text-stone-900">{submission.signature}</span>
          </Row>
          <Row icon={CalendarClock} label="Submitted">{fmt(submission.submittedAt)}</Row>
          {submission.notes && <Row icon={Mail} label="Notes from guest">{submission.notes}</Row>}
          <Row icon={MapPin} label="IP address">{submission.ipAddress || '—'}</Row>
          <Row icon={Monitor} label="User agent">
            <span className="text-[11px] text-stone-500 break-all">{submission.userAgent || '—'}</span>
          </Row>
        </div>

        <DialogFooter className="px-5 py-3 border-t border-stone-200 bg-stone-50">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <a href={Release.pdfUrl(submission.id)} target="_blank" rel="noreferrer">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
            </Button>
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
